import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, isValid } from 'date-fns';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

import * as BackupApi from '../../../api/connectionBackups.js';

import { Button as CmpButton } from '../../../components/Button';

const triggerText = { Daily: 'Daily', BeforeImport: 'Before import', Manual: 'Manual' };

export const backupTakenText = (createdDate) => {
  const date = new Date(createdDate);
  return isValid(date) ? format(date, 'd MMM yyyy, HH:mm') : 'an unknown time';
};

const selectClass = 'block rounded-md border border-gray-300 bg-white py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue dark:bg-gray-700 dark:border-gray-600 dark:text-white';

const failure = (err, fallback) => err?.response?.status === 403 ? 'You do not have access to backups.' : (err?.response?.data?.Message || err?.response?.data?.message || fallback);

/**
 * The backups of one environment of the group, to restore from or download. `environments` is every environment of the
 * group as { id, name }; `onRestore(backup, document, environment)` gets the backup the person chose to restore.
 */
export default function ConnectionBackups({ groupId, environments, defaultEnvironmentId, onRestore }) {
  const [environmentId, setEnvironmentId] = useState(defaultEnvironmentId || environments[0]?.id);
  const [settings, setSettings] = useState(null);
  const [backups, setBackups] = useState(null);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const environment = environments.find(e => e.id === environmentId);

  const loadBackups = async (id) => {
    setBackups(null);
    setError(null);
    try {
      const list = await BackupApi.getBackups(id);
      if (mounted.current)
        setBackups(list);
    } catch (err) {
      if (mounted.current)
        setError(failure(err, 'The backups could not be loaded.'));
    }
  };

  useEffect(() => {
    BackupApi.getBackupSettings()
      .then(all => mounted.current && setSettings(all.find(s => s.environmentGroupId === groupId) || { enabled: false, keepCount: 30 }))
      .catch(() => mounted.current && setSettings({ enabled: false, keepCount: 30, unknown: true }));
  }, [groupId]);

  useEffect(() => {
    if (environmentId)
      loadBackups(environmentId);
  }, [environmentId]);

  const restore = async (backup) => {
    setWorking(backup.id);
    setError(null);
    try {
      const { document } = await BackupApi.getBackup(backup.id);
      if (mounted.current)
        onRestore(backup, document, environment);
    } catch (err) {
      if (mounted.current)
        setError(failure(err, 'The backup could not be opened.'));
    } finally {
      if (mounted.current)
        setWorking(null);
    }
  };

  const download = async (backup) => {
    setWorking(backup.id);
    setError(null);
    try {
      const { document } = await BackupApi.getBackup(backup.id);
      saveAs(new Blob([JSON.stringify(document, null, 2)], { type: 'application/json' }), `connections-${environment?.name || 'environment'}-backup-${format(new Date(backup.createdDate), 'yyyyMMdd-HHmmss')}.json`);
    } catch (err) {
      if (mounted.current)
        setError(failure(err, 'The backup could not be downloaded.'));
    } finally {
      if (mounted.current)
        setWorking(null);
    }
  };

  const backUpNow = async () => {
    setWorking('now');
    setError(null);
    try {
      const { outcome } = await BackupApi.backUpNow(environmentId);
      toast.success(outcome === 'Stored' ? 'Backup taken' : outcome === 'Unchanged' ? 'The newest backup already matches the current connections' : 'The backup was too large to store');
      await loadBackups(environmentId);
    } catch (err) {
      if (mounted.current)
        setError(failure(err, 'The backup could not be taken.'));
    } finally {
      if (mounted.current)
        setWorking(null);
    }
  };

  return (
    <div id="connection-backups">
      {settings && !settings.enabled && !settings.unknown && (
        <p id="backups-off" role="status" className="mb-4 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
          Backups are off for this environment group, so no new backups are taken. Switch them on in <Link className="font-semibold underline" to="/admin/config">Admin</Link>. Backups taken earlier can still be restored.
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <label htmlFor="backup-environment" className="block text-sm font-medium text-gray-900 dark:text-gray-200">Backups of</label>
          <select id="backup-environment" className={selectClass} value={environmentId} disabled={working !== null}
            onChange={e => setEnvironmentId(e.target.value)}>
            {environments.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        {settings?.enabled && (
          <CmpButton id="backup-now" variant={CmpButton.variants.secondary} disabled={working !== null} onClick={backUpNow}>
            {working === 'now' ? 'Backing up…' : `Back up ${environment?.name || 'this environment'} now`}
          </CmpButton>
        )}
      </div>

      {error && <div role="alert" className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900 dark:text-red-100">{error}</div>}

      {backups === null && !error && <p role="status" className="text-sm text-gray-600 dark:text-gray-400">Loading the backups…</p>}

      {backups?.length === 0 && <p id="no-backups" className="text-sm text-gray-600 dark:text-gray-400">There are no backups of {environment?.name} yet.</p>}

      {backups?.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table id="backup-list" className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-4 py-2">Backup taken</th>
                <th scope="col" className="px-4 py-2">Why</th>
                <th scope="col" className="px-4 py-2">Logic files</th>
                <th scope="col" className="px-4 py-2">Connections</th>
                <th scope="col" className="px-4 py-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900 text-gray-900 dark:text-gray-200">
              {backups.map(backup => (
                <tr key={backup.id}>
                  <td className="whitespace-nowrap px-4 py-2">{backupTakenText(backup.createdDate)}</td>
                  <td className="px-4 py-2">{triggerText[backup.trigger] || backup.trigger}</td>
                  <td className="px-4 py-2">{backup.fileCount}</td>
                  <td className="px-4 py-2">{backup.connectionCount}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    <span className="inline-flex gap-2">
                      <CmpButton variant={CmpButton.variants.secondary} disabled={working !== null} onClick={() => download(backup)}>Download</CmpButton>
                      <CmpButton variant={CmpButton.variants.primary} disabled={working !== null} onClick={() => restore(backup)}>
                        {working === backup.id ? 'Opening…' : 'Restore…'}
                      </CmpButton>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {settings && !settings.unknown && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Keeps the last {settings.keepCount} backups of each environment. Change this in <Link className="underline" to="/admin/config">Admin</Link>.
        </p>
      )}
    </div>
  );
}
